import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster, ToastBar, toast } from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import Footer from "./components/Footer";
import NotificationsButton from "./components/NotificationsButton";
import PrivacyConsentGate from "./components/PrivacyConsentGate";
import Login from "./pages/Login";
import RichiestaCongregazione from "./pages/RichiestaCongregazione";
import Dashboard from "./pages/Dashboard";
import ElencoPostazioni from "./pages/ElencoPostazioni";
import GestionePostazioni from "./pages/GestionePostazioni";
import ElencoVolontari from "./pages/ElencoVolontari";
import GestioneVolontari from "./pages/GestioneVolontari";
import TurniConduttori from "./pages/TurniConduttori";
import DisponibilitaConduttori from "./pages/DisponibilitaConduttori";
import RiepilogoDisponibilita from "./pages/RiepilogoDisponibilita";
import GestioneTurni from "./pages/GestioneTurni";
import TurniIncompleti from "./pages/TurniIncompleti";
import Notifiche from "./pages/Notifiche";
import Cronologia from "./pages/Cronologia";
import CronologiaStatistiche from "./pages/CronologiaStatistiche";
import Assistenza from "./pages/Assistenza";
import Impostazioni from "./pages/Impostazioni";
import Congregazioni from "./pages/Congregazioni";
import IMieiTurni from "./pages/IMieiTurni";
import Esperienze from "./pages/Esperienze";
import Autorizzazioni from "./pages/Autorizzazioni";
import Istruzioni from "./pages/Istruzioni";
import ContattoResponsabile from "./pages/ContattoResponsabile";
import ScegliServizio from "./pages/ScegliServizio";
import Deleghe from "./pages/Deleghe";
import ConsensiPrivacy from "./pages/ConsensiPrivacy";
import AdunanzeServizio from "./pages/AdunanzeServizio";
import AdunanzeSettimanali from "./pages/AdunanzeSettimanali";
import Eventi from "./pages/Eventi";
import RapportiServizio from "./pages/RapportiServizio";
import RegistroServizio from "./pages/RegistroServizio";
import GruppiServizio from "./pages/GruppiServizio";
import TurniPulizie from "./pages/TurniPulizie";
import DiscorsiPubblici from "./pages/DiscorsiPubblici";
import ForcePasswordChangeModal from "./components/ForcePasswordChangeModal";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./index.css";
import { ensurePushSubscription, getPermissionStatus, isPushSupported } from "./utils/push";

// Lazy load per Disponibilita
const Disponibilita = lazy(() => import("./pages/Disponibilita"));

// Componente per il layout protetto
const ProtectedLayout = ({ children }) => {
  const location = useLocation();
  const {
    isAuthenticated,
    loading,
    user,
    activeCongregazione,
    acceptPrivacyPolicy,
    pushInvite,
    ackPushInvite,
    refreshPushStatus,
  } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [showPushHelp, setShowPushHelp] = React.useState(false);
  // Inizializza isMobile controllando immediatamente la dimensione dello schermo
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768; // md breakpoint di Tailwind
    }
    return false;
  });
  const [pushInviteLoading, setPushInviteLoading] = React.useState(false);
  const handleSidebarClose = React.useCallback(() => setSidebarOpen(false), []);
  const handleSidebarOpen = React.useCallback(() => setSidebarOpen(true), []);

  // Rileva se siamo su mobile e aggiorna lo stato
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const [acceptingPrivacy, setAcceptingPrivacy] = React.useState(false);
  const needsPrivacyConsent =
    isAuthenticated && !user?.privacy_policy_accepted_at;

  const handlePrivacyAccept = React.useCallback(async () => {
    try {
      setAcceptingPrivacy(true);
      const result = await acceptPrivacyPolicy();
      if (!result?.success) {
        throw new Error(result?.error || "Salvataggio non riuscito");
      }
    } catch (error) {
      console.error("Errore accettazione privacy:", error);
      toast.error(
        "Impossibile salvare l'accettazione al momento. Riprova tra poco."
      );
    } finally {
      setAcceptingPrivacy(false);
    }
  }, [acceptPrivacyPolicy]);

  const handleAcceptPushInvite = React.useCallback(async () => {
    if (!isPushSupported()) {
      toast.error("Notifiche push non supportate su questo dispositivo");
      return;
    }
    const perm = getPermissionStatus();
    if (perm === "denied") {
      toast.error(
        "Permesso notifiche negato. Abilitalo dalle impostazioni del browser."
      );
      return;
    }
    try {
      setPushInviteLoading(true);
      await ensurePushSubscription({
        congregazioneId: activeCongregazione?.id,
      });
      await refreshPushStatus();
      await ackPushInvite();
      toast.success("Notifiche push attivate su questo dispositivo");
    } catch (error) {
      console.error("Errore attivazione push da invito:", error);
      toast.error(
        error?.message ||
          "Impossibile attivare le notifiche push su questo dispositivo"
      );
    } finally {
      setPushInviteLoading(false);
    }
  }, [activeCongregazione?.id, ackPushInvite, refreshPushStatus]);

  // Mostra loading screen mentre verifica il token
  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifica autenticazione...</p>
        </div>
      </div>
    );
  }

  // Reindirizza al login se non autenticato
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const needsForcePasswordChange = user?.force_password_change === true;

  const handlePasswordChanged = () => {
    // Reload the page to refresh user data and clearforce_password_change flag
    window.location.reload();
  };

  return (
    <div className="flex bg-gray-100 min-h-screen md:h-screen overflow-x-hidden">
      {needsForcePasswordChange && (
        <ForcePasswordChangeModal onPasswordChanged={handlePasswordChanged} />
      )}
      {needsPrivacyConsent && location.pathname !== "/consensi-privacy" && (
        <PrivacyConsentGate
          accepting={acceptingPrivacy}
          onAccept={handlePrivacyAccept}
        />
      )}
      <Sidebar isOpen={sidebarOpen} onClose={handleSidebarClose} />
      <div className="flex flex-col flex-1 md:overflow-hidden md:ml-0 min-w-0">
        {/* Header mobile con bottone menu */}
        {isMobile && (
          <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-30">
            <button
              onClick={handleSidebarOpen}
              className="p-2 text-gray-600 hover:text-gray-900"
              aria-label="Apri menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Planner Turni</h1>
            <div className="flex items-center gap-3">
              <NotificationsButton />
            </div>
          </div>
        )}
        {!isMobile && (
          <div className="hidden md:flex items-center justify-end bg-white border-b border-gray-200 px-6 py-3">
            <NotificationsButton />
          </div>
        )}
        <main className="flex-1 md:overflow-y-auto min-w-0 w-full overflow-x-hidden">
          {pushInvite?.pending && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="text-sm">
                <div className="font-semibold text-base">Attiva le notifiche push</div>
                <div className="text-sm leading-snug">
                  {pushInvite.message ||
                    "Un amministratore ti ha invitato ad attivare le notifiche push per ricevere aggiornamenti."}
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 w-full md:w-auto">
                <button
                  type="button"
                  onClick={handleAcceptPushInvite}
                  disabled={pushInviteLoading}
                  className="w-full md:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm font-semibold"
                >
                  {pushInviteLoading ? "Attivazione..." : "Attiva ora"}
                </button>
                <button
                  type="button"
                  onClick={ackPushInvite}
                  className="w-full md:w-auto px-4 py-2 border border-blue-200 text-blue-800 rounded-md hover:bg-blue-100 text-sm font-semibold"
                >
                  Ignora
                </button>
                <button
                  type="button"
                  onClick={() => setShowPushHelp((prev) => !prev)}
                  className="w-full md:w-auto px-4 py-2 border border-blue-200 text-blue-800 rounded-md hover:bg-blue-100 text-sm font-semibold"
                >
                  Istruzioni
                </button>
              </div>
              {showPushHelp && (
                <div className="bg-white/70 border border-blue-200 rounded-md p-3 text-gray-800 text-xs space-y-3">
                  <div className="font-semibold text-sm">Come attivare le push</div>
                  <div>
                    <div className="font-semibold">Da smartphone Android</div>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Apri il sito da Chrome.</li>
                      <li>Menu ⋮ → “Aggiungi a schermata Home”.</li>
                      <li>Apri l’icona creata e consenti le notifiche quando richiesto.</li>
                      <li>Premi “Attiva ora”.</li>
                    </ol>
                  </div>
                  <div>
                    <div className="font-semibold">Da iPhone</div>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Apri il sito da Safari.</li>
                      <li>Tocca Condividi → “Aggiungi a Home” (necessario per le push su iOS).</li>
                      <li>Apri l’icona sulla Home e consenti le notifiche quando richiesto.</li>
                      <li>Premi “Attiva ora”.</li>
                    </ol>
                  </div>
                  <div>
                    <div className="font-semibold">Da PC/Mac</div>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Apri il sito su Chrome/Edge/Firefox.</li>
                      <li>Consenti le notifiche dal lucchetto/permessi vicino alla barra indirizzi.</li>
                      <li>(Opzionale) Chrome: menu ⋮ → “Installa app”.</li>
                      <li>Premi “Attiva ora”.</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          )}
          {user?.ruolo === "super_admin" &&
            !activeCongregazione?.id &&
            location.pathname !== "/congregazioni" && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3">
                Seleziona una congregazione attiva dalla sezione <strong>Congregazioni</strong> per operare sui dati.
              </div>
            )}
          {children}
        </main>
        <Footer />
      </div>
    </div>
  );
};

// Componente per le pagine protette
const ProtectedRoute = ({ children }) => {
  return <ProtectedLayout>{children}</ProtectedLayout>;
};

const SuperAdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.ruolo !== "super_admin") {
    return <Navigate to="/" replace />;
  }
  return <ProtectedLayout>{children}</ProtectedLayout>;
};

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App">
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#363636",
                color: "#fff",
                borderRadius: "8px",
                padding: "12px 16px",
              },
              dismissible: true,
              className: "custom-toast",
              ariaProps: {
                role: "status",
                "aria-live": "polite",
              },
            }}
            containerStyle={{
              maxWidth: "550px",
              minWidth: "350px",
              bottom: "24px",
              right: "24px",
              top: "auto",
              left: "auto",
            }}
            reverseOrder={false}
            gutter={12}
          >
            {(t) => (
              <ToastBar
                toast={t}
                style={{
                  width: "100%",
                }}
              >
                {({ icon, message }) => (
                  <div className="flex w-full items-start space-x-3">
                    {icon && <div className="mt-0.5">{icon}</div>}
                    <div className="flex-1 text-sm leading-relaxed">{message}</div>
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="ml-2 text-white/70 hover:text-white focus:outline-none"
                      aria-label="close"
                    >
                      ×
                    </button>
                  </div>
                )}
              </ToastBar>
            )}
          </Toaster>

          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/richiedi-congregazione"
              element={<RichiestaCongregazione />}
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scegli-servizio"
              element={
                <ProtectedRoute>
                  <ScegliServizio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/miei-turni"
              element={
                <ProtectedRoute>
                  <IMieiTurni />
                </ProtectedRoute>
              }
            />
            <Route
              path="/postazioni"
              element={
                <ProtectedRoute>
                  <ElencoPostazioni />
                </ProtectedRoute>
              }
            />
            <Route
              path="/postazioni/gestione"
              element={
                <ProtectedRoute>
                  <GestionePostazioni />
                </ProtectedRoute>
              }
            />
            <Route
              path="/volontari"
              element={
                <ProtectedRoute>
                  <ElencoVolontari />
                </ProtectedRoute>
              }
            />
            <Route
              path="/volontari/gestione"
              element={
                <ProtectedRoute>
                  <GestioneVolontari />
                </ProtectedRoute>
              }
            />
            <Route
              path="/volontari/disponibilita"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-4">Caricamento disponibilità...</div>}>
                    <Disponibilita />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/volontari/riepilogo-disponibilita"
              element={
                <ProtectedRoute>
                  <RiepilogoDisponibilita />
                </ProtectedRoute>
              }
            />
            <Route
              path="/volontari/rapporti-servizio"
              element={
                <ProtectedRoute>
                  <RapportiServizio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/turni/gestione"
              element={
                <ProtectedRoute>
                  <GestioneTurni />
                </ProtectedRoute>
              }
            />
            <Route
              path="/turni/incompleti"
              element={
                <ProtectedRoute>
                  <TurniIncompleti />
                </ProtectedRoute>
              }
            />
            <Route
              path="/turni/conduttori"
              element={
                <ProtectedRoute>
                  <TurniConduttori />
                </ProtectedRoute>
              }
            />
            <Route
              path="/turni/pulizie"
              element={
                <ProtectedRoute>
                  <TurniPulizie />
                </ProtectedRoute>
              }
            />
            <Route
              path="/esperienze"
              element={
                <ProtectedRoute>
                  <Esperienze />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documenti/autorizzazioni"
              element={
                <ProtectedRoute>
                  <Autorizzazioni />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documenti/istruzioni"
              element={
                <ProtectedRoute>
                  <Istruzioni />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documenti/contatto-responsabile"
              element={
                <ProtectedRoute>
                  <ContattoResponsabile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifiche"
              element={
                <ProtectedRoute>
                  <Notifiche />
                </ProtectedRoute>
              }
            />
            <Route
              path="/registro"
              element={<Navigate to="/registro/turni-passati" replace />}
            />
            <Route
              path="/registro/turni-passati"
              element={
                <ProtectedRoute>
                  <Cronologia />
                </ProtectedRoute>
              }
            />
            <Route
              path="/registro/statistiche"
              element={
                <ProtectedRoute>
                  <CronologiaStatistiche />
                </ProtectedRoute>
              }
            />
            <Route
              path="/registro/eventi"
              element={
                <ProtectedRoute>
                  <Eventi />
                </ProtectedRoute>
              }
            />
            <Route
              path="/registro/servizio"
              element={
                <ProtectedRoute>
                  <RegistroServizio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cronologia/*"
              element={<Navigate to="/registro/turni-passati" replace />}
            />
            <Route
              path="/assistenza"
              element={
                <ProtectedRoute>
                  <Assistenza />
                </ProtectedRoute>
              }
            />
            <Route
              path="/adunanze"
              element={
                <ProtectedRoute>
                  <AdunanzeSettimanali />
                </ProtectedRoute>
              }
            />
            <Route
              path="/adunanze-servizio"
              element={
                <ProtectedRoute>
                  <AdunanzeServizio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/adunanze-servizio/disponibilita"
              element={
                <ProtectedRoute>
                  <DisponibilitaConduttori />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consensi-privacy"
              element={
                <ProtectedRoute>
                  <ConsensiPrivacy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/volontari/deleghe"
              element={
                <ProtectedRoute>
                  <Deleghe />
                </ProtectedRoute>
              }
            />
            <Route
              path="/volontari/gruppi-servizio"
              element={
                <ProtectedRoute>
                  <GruppiServizio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discorsi-pubblici/*"
              element={
                <ProtectedRoute>
                  <DiscorsiPubblici />
                </ProtectedRoute>
              }
            />
            <Route
              path="/congregazioni"
              element={
                <SuperAdminRoute>
                  <Congregazioni />
                </SuperAdminRoute>
              }
            />
            <Route
              path="/impostazioni"
              element={
                <ProtectedRoute>
                  <Impostazioni />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
